import React, { memo, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { dates, formats, validators } from 'investira.sdk';
import {
  Button,
  Stack,
  InputAdornment,
  MenuItem,
  TextField,
  Icon,
  LoadingButton,
  Skeleton,
  FormControlLabel,
  Autocomplete,
  Typography,
  Alert,
  FormControl,
  RadioGroup,
  Radio,
  ListItemText,
  Chip,
  Tabs,
  Tab,
  Box
} from 'investira.react.components';
import { VALIDATIONS } from '../../../const';
import { useRelatorioContext } from '../../../contexts/RelatorioContext';
import services from '../../../services';
import withResponseHandling from '../../../hoc/withResponseHandling';
import withSaveParams from '../../../hoc/withSaveParams';

const VALIDATION_SCHEMA = Yup.object().shape({
  tipo_selecao: Yup.string().oneOf(['carteira', 'portfolio']).required(VALIDATIONS.REQUIRED),
  carteira_id: Yup.string().when('tipo_selecao', {
    is: 'carteira',
    then: Yup.string().required(VALIDATIONS.REQUIRED),
    otherwise: Yup.string().nullable()
  }),
  portfolio_id: Yup.string().when('tipo_selecao', {
    is: 'portfolio',
    then: Yup.string().required(VALIDATIONS.REQUIRED),
    otherwise: Yup.string().nullable()
  }),
  data: Yup.string().when('tipo_selecao', {
    is: 'carteira',
    then: Yup.string().required(VALIDATIONS.REQUIRED).nullable(),
    otherwise: Yup.string().nullable()
  }),
  enquadramento_id: Yup.string().required(VALIDATIONS.REQUIRED)
});

const EnquadramentoParams = memo(props => {
  const { state, actions } = useRelatorioContext();

  const navigate = useNavigate();
  const location = useLocation();

  const storedParams = props.storedParams('enquadramento', props.lastOutput);

  const [datas, setDatas] = useState([]);
  const [enquadramentos, setEnquadramentos] = useState([]);

  const [isLoadingEnquadramentos, setIsLocadingEnquadramentos] = useState(true);

  const xInitialValues = {
    tipo_selecao: storedParams.tipo_selecao || 'carteira',
    carteira_id: '',
    portfolio_id: '',
    data: storedParams.tipo_selecao === 'portfolio' ? null : storedParams.data || null,
    aberta: true,
    enquadramento_id: '',
    consolidacao_por_emissor: 'I'
  };

  const formik = useFormik({
    initialValues: xInitialValues,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (pValues, pActions) => {
      services.outputs.enquadramento(
        {
          ...pValues
        },
        rRes => {
          const { output_cache_id } = rRes.data;
          navigate(`${location.pathname}/${output_cache_id}`);
          actions.handleNewConsultCancel();
          props.saveParams('enquadramento', pValues);
        },
        rErr => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    }
  });

  function handleChangeEnquadramento(_e, pValue) {
    formik.setFieldValue('enquadramento_id', pValue?.enquadramento_id || '');
  }

  useEffect(() => {
    const xValues = storedParams;
    if (!validators.isEmpty(xValues)) {
      const { carteira_id, portfolio_id, consolidacao_por_emissor, tipo_selecao } = xValues;
      formik.setFieldValue(
        'tipo_selecao',
        tipo_selecao || (portfolio_id ? 'portfolio' : 'carteira')
      );
      formik.setFieldValue('carteira_id', carteira_id || '');
      formik.setFieldValue('portfolio_id', portfolio_id || '');
      formik.setFieldValue('consolidacao_por_emissor', consolidacao_por_emissor);
    }
  }, []);

  useEffect(() => {
    services.enquadramentos.list(
      { data: formik.values.data },
      rRes => {
        const xEnquadramentos = Object.values(rRes.data).map(xItem => {
          return {
            ...xItem,
            label: `${xItem.enquadramento}`
          };
        });

        setEnquadramentos(xEnquadramentos);

        const xValues = storedParams;
        if (!validators.isEmpty(xValues)) {
          const { enquadramento_id } = xValues;
          formik.setFieldValue('enquadramento_id', enquadramento_id);
        }
      },
      rErr => {
        props.responseErrorHandling(rErr);
      },
      () => {
        setIsLocadingEnquadramentos(false);
      }
    );
  }, [formik.values.data]);

  useEffect(() => {
    if (state.isDialogOpen && formik.values.tipo_selecao === 'carteira') {
      setDatas([]);

      formik.values.carteira_id &&
        services.posicoes.datas(
          {
            sort: 'data DESC',
            carteira_id: formik.values.carteira_id
          },
          rRes => {
            const xDates = rRes.data.map(xPosicao => {
              return {
                value: formats.formatDateCustom(dates.toDate(xPosicao.data), 'YYYY-MM-DD'),
                label: formats.formatDateCustom(dates.toDate(xPosicao.data), 'DD/MM/YYYY'),
                aberta: xPosicao.aberta
              };
            });
            setDatas(xDates);

            const xValues = storedParams;

            if (!validators.isEmpty(xValues) && formik.values.carteira_id === xValues.carteira_id) {
              const { data } = xValues;
              formik.setFieldValue('data', data);
            } else {
              formik.setFieldValue('data', xDates[0].value);
            }
          },
          rErr => {
            props.responseErrorHandling(rErr);
          }
        );
    }
  }, [state.isDialogOpen, formik.values.carteira_id, formik.values.tipo_selecao]);

  const xEnquadramentoValue =
    enquadramentos.filter(
      xEnquadramento => xEnquadramento.enquadramento_id === formik.values.enquadramento_id
    )[0] || null;

  const xCarteiras = useMemo(
    () => props.carteiras.slice().sort((a, b) => a.carteira.localeCompare(b.carteira)),
    [props.carteiras]
  );

  const xPortfolios = useMemo(
    () => (props.portfolios || []).slice().sort((a, b) => a.portfolio.localeCompare(b.portfolio)),
    [props.portfolios]
  );

  const xDataSelected = datas.find(xData => xData.value === formik.values.data);

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack sx={{ width: '500px', marginBottom: 2, py: 1 }} spacing={2}>
        {process.env.REACT_APP_ENV !== 'prd' && (
          <Stack flexGrow={1}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={formik.values.tipo_selecao || 'carteira'}
                onChange={(_event, pValue) => {
                  if (!pValue) return;
                  formik.setFieldValue('tipo_selecao', pValue);
                  formik.setFieldValue('carteira_id', '');
                  formik.setFieldValue('portfolio_id', '');
                  if (pValue === 'portfolio') {
                    formik.setFieldValue('data', null);
                  }
                }}
                sx={{ alignSelf: 'center' }}>
                <Tab value="carteira" label="Carteira" />
                <Tab value="portfolio" label="Portfólio" />
              </Tabs>
            </Box>
          </Stack>
        )}

        {/* NOTE: Talvez seja necessário trocar esse select  por um calendar */}

        <Stack flexGrow={1}>
          {formik.values.tipo_selecao === 'carteira' ? (
            <TextField
              id="carteira_id"
              name="carteira_id"
              label="Carteira"
              variant="outlined"
              value={formik.values.carteira_id}
              onChange={formik.handleChange}
              select
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon iconName="wallet" size={21} />
                  </InputAdornment>
                )
              }}>
              {xCarteiras.map(xOption => (
                <MenuItem key={xOption.carteira_id} value={xOption.carteira_id}>
                  {xOption.carteira}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              id="portfolio_id"
              name="portfolio_id"
              label="Portfólio"
              variant="outlined"
              value={formik.values.portfolio_id}
              onChange={formik.handleChange}
              select
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon iconName="wallet" size={21} />
                  </InputAdornment>
                )
              }}>
              {xPortfolios.map(xOption => (
                <MenuItem key={xOption.portfolio_id} value={xOption.portfolio_id}>
                  {xOption.portfolio}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {formik.values.carteira_id && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(dates) ? (
                  <Skeleton variant="rounded" animation="wave" height={32} />
                ) : (
                  <TextField
                    id="data"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={formik.handleChange}
                    sx={{
                      '& .MuiChip-root': {
                        display: 'none'
                      }
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {xDataSelected?.aberta && (
                            <Chip
                              icon={<Icon iconName="box_3d_open" size={18} />}
                              label={'Aberta'}
                              color="warning"
                              size="small"
                              sx={{ px: 1, display: 'flex !important', mr: 4 }}
                              variant="filled"
                            />
                          )}
                        </InputAdornment>
                      )
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}>
                    {datas.map(xOption => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && (
                          <Chip
                            icon={<Icon iconName="box_3d_open" size={18} />}
                            label={'Aberta'}
                            color="warning"
                            size="small"
                            sx={{ px: 1 }}
                            variant="filled"
                          />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {formik.values.portfolio_id && (
          <>
            <Stack direction="row" spacing={2}>
              <Stack flexGrow={1} flexBasis={1}>
                {validators.isEmpty(dates) ? (
                  <Skeleton variant="rounded" animation="wave" height={32} />
                ) : (
                  <TextField
                    id="data"
                    name="data"
                    label="Posição de"
                    variant="outlined"
                    value={formik.values.data}
                    onChange={formik.handleChange}
                    sx={{
                      '& .MuiChip-root': {
                        display: 'none'
                      }
                    }}
                    select
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon iconName="calendar" size={21} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {xDataSelected?.aberta && (
                            <Chip
                              icon={<Icon iconName="box_3d_open" size={18} />}
                              label={'Aberta'}
                              color="warning"
                              size="small"
                              sx={{ px: 1, display: 'flex !important', mr: 4 }}
                              variant="filled"
                            />
                          )}
                        </InputAdornment>
                      )
                    }}
                    error={Boolean(formik.errors.data)}
                    helperText={formik.errors.data}
                    disabled={datas.length <= 1}>
                    {/* TODO: Exibir as datas em formato de lista com rolagem, 
                    sinalizar as datas quando as dasta das posições não estiverem sincronizadas. 
                    Ter um ícone de info, para exibir a data das carteiras em dissicronia */}

                    {/* {datas.map(xOption => (
                      <MenuItem key={xOption.value} value={xOption.value}>
                        <ListItemText>{xOption.label}</ListItemText>
                        {xOption.aberta && (
                          <Chip
                            icon={<Icon iconName="box_3d_open" size={18} />}
                            label={'Aberta'}
                            color="warning"
                            size="small"
                            sx={{ px: 1 }}
                            variant="filled"
                          />
                        )}
                      </MenuItem>
                    ))} */}
                  </TextField>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {!isLoadingEnquadramentos && validators.isEmpty(enquadramentos) && (
          <Stack flexGrow={1}>
            <Alert severity="warning">Necessário configurar um enquadramento para continuar.</Alert>
          </Stack>
        )}

        {formik.values.carteira_id && !validators.isEmpty(enquadramentos) && (
          <Stack flexGrow={1}>
            <Autocomplete
              disablePortal
              options={enquadramentos}
              noOptionsText={'Nenhum Enquadramento Encontrado'}
              autoHighlight={true}
              onChange={handleChangeEnquadramento}
              value={xEnquadramentoValue}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={crypto.randomUUID()} {...optionProps}>
                    <Stack direction="row" flexGrow={1} spacing={3} alignItems="flex-start">
                      <Stack flexGrow={1}>
                        <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
                          {option.label}
                        </Typography>
                      </Stack>
                    </Stack>
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Enquadramento"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              )}
            />
          </Stack>
        )}

        {formik.values.carteira_id && (
          <Stack flexGrow={1} sx={{ marginLeft: '16px !important' }}>
            <FormControl>
              <Typography id="consolidacao_por_emissor" variant="caption" color="textSecondary">
                Consolidação por Emissor
              </Typography>
              <RadioGroup
                aria-labelledby="consolidacao_por_emissor"
                value={formik.values.consolidacao_por_emissor}
                id="consolidacao_por_emissor"
                name="consolidacao_por_emissor">
                <FormControlLabel
                  value="I"
                  control={<Radio size="small" />}
                  label="Considerar somente os ativos desta carteira"
                  onChange={formik.handleChange}
                  componentsProps={{
                    typography: {
                      variant: 'caption'
                    }
                  }}
                />
                <FormControlLabel
                  value="P"
                  control={<Radio size="small" />}
                  label="Considerar os ativos de todas as carteiras próprias"
                  onChange={formik.handleChange}
                  componentsProps={{
                    typography: {
                      variant: 'caption'
                    }
                  }}
                />
                {/* <FormControlLabel
                  value="F"
                  control={<Radio size="small" />}
                  label="Considerar os ativos de todas as carteiras próprias do portfolio"
                  onChange={formik.handleChange}
                  componentsProps={{
                    typography: {
                      variant: "caption",
                    },
                  }}
                /> */}
              </RadioGroup>
            </FormControl>
          </Stack>
        )}

        <Stack alignItems="center" justifyContent="flex-end" direction="row" spacing={2}>
          <Button
            variant="outlined"
            type="button"
            color="primary"
            onClick={actions.handleNewConsultCancel}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={formik.isSubmitting}
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              (validators.isEmpty(formik.values.carteira_id) &&
                validators.isEmpty(formik.values.portfolio_id))
            }>
            Processar
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
});

export default withResponseHandling(withSaveParams(EnquadramentoParams));
